/**
 * Document Reviews Queries
 * CRUD operations for document review system
 */

import { supabase } from "../supabase-client";

export type AnalysisStatus = "pending" | "processing" | "completed" | "failed";

export interface RiskItem {
  severity: "low" | "medium" | "high" | "critical";
  description: string;
  location?: string;
  recommendation?: string;
}

export interface OmissionItem {
  description: string;
  recommendation: string;
  importance: "low" | "medium" | "high";
}

export interface SensitiveClause {
  clause: string;
  concern: string;
  suggestion: string;
  severity: "low" | "medium" | "high";
}

export interface AnalysisResult {
  overall_risk: "low" | "medium" | "high";
  risks: RiskItem[];
  omissions: OmissionItem[];
  sensitive_clauses: SensitiveClause[];
  recommendations: string[];
  summary: string;
  confidence_score?: number;
}

export interface DocumentReview {
  id: string;
  user_id: string;
  document_name: string;
  /** Upload filename; required on some DBs (staging) as NOT NULL column */
  original_filename?: string;
  document_type: string;
  country: string;
  file_path: string;
  file_url: string;
  file_size?: number;
  file_mime_type?: string;
  original_content?: string;
  analysis_status: AnalysisStatus;
  analysis_result?: AnalysisResult;
  credits_used: number;
  created_at: string;
  updated_at: string;
  analyzed_at?: string;
}

export interface CreateDocumentReviewParams {
  user_id: string;
  document_name: string;
  /** Defaults to document_name when omitted (must match NOT NULL original_filename on DB) */
  original_filename?: string;
  document_type: string;
  country: string;
  file_path: string;
  file_url: string;
  file_size?: number;
  file_mime_type?: string;
  original_content?: string;
  credits_used?: number;
}

export interface UpdateReviewAnalysisParams {
  id: string;
  analysis_result: AnalysisResult;
  analysis_status: AnalysisStatus;
  analyzed_at?: string;
}

/**
 * Create a new document review record
 */
export async function createDocumentReview(
  params: CreateDocumentReviewParams
): Promise<DocumentReview> {
  const originalFilename =
    params.original_filename?.trim() ||
    params.document_name?.trim() ||
    "document";

  const { data, error } = await supabase
    .from("document_reviews" as any)
    .insert({
      user_id: params.user_id,
      document_name: params.document_name,
      original_filename: originalFilename,
      document_type: params.document_type,
      country: params.country,
      file_path: params.file_path,
      file_url: params.file_url,
      file_size: params.file_size,
      file_mime_type: params.file_mime_type,
      original_content: params.original_content,
      credits_used: params.credits_used || 1,
      analysis_status: "pending",
    })
    .select()
    .single();

  if (error) {
    console.error("Error creating document review:", error);
    throw new Error(`Error al crear la revisión: ${error.message}`);
  }

  return data as unknown as DocumentReview;
}

/**
 * Update document review with analysis result
 */
export async function updateReviewAnalysis(
  params: UpdateReviewAnalysisParams
): Promise<DocumentReview> {
  const { data, error } = await supabase
    .from("document_reviews" as any)
    .update({
      analysis_result: params.analysis_result,
      analysis_status: params.analysis_status,
      analyzed_at: params.analyzed_at || new Date().toISOString(),
    })
    .eq("id", params.id)
    .select()
    .single();

  if (error) {
    console.error("Error updating review analysis:", error);
    throw new Error(`Error al actualizar el análisis: ${error.message}`);
  }

  return data as unknown as DocumentReview;
}

/**
 * Update review status (e.g., to processing or failed)
 */
export async function updateReviewStatus(
  id: string,
  status: AnalysisStatus
): Promise<DocumentReview> {
  const { data, error } = await supabase
    .from("document_reviews" as any)
    .update({ analysis_status: status })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating review status:", error);
    throw new Error(`Error al actualizar el estado: ${error.message}`);
  }

  return data as unknown as DocumentReview;
}

/**
 * Get a document review by ID
 */
export async function getDocumentReviewById(
  id: string
): Promise<DocumentReview | null> {
  const { data, error } = await supabase
    .from("document_reviews" as any)
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      // Not found
      return null;
    }
    console.error("Error fetching document review:", error);
    throw new Error(`Error al obtener la revisión: ${error.message}`);
  }

  return data as unknown as DocumentReview;
}

/**
 * Get all document reviews for a user
 */
export async function getUserDocumentReviews(
  userId: string,
  options?: {
    limit?: number;
    offset?: number;
    country?: string;
    documentType?: string;
    status?: AnalysisStatus;
  }
): Promise<DocumentReview[]> {
  let query = supabase
    .from("document_reviews" as any)
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });

  // Apply filters
  if (options?.country) {
    query = query.eq("country", options.country);
  }

  if (options?.documentType) {
    query = query.eq("document_type", options.documentType);
  }

  if (options?.status) {
    query = query.eq("analysis_status", options.status);
  }

  // Apply pagination
  if (options?.limit) {
    query = query.limit(options.limit);
  }

  if (options?.offset) {
    query = query.range(
      options.offset,
      options.offset + (options.limit || 10) - 1
    );
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching user document reviews:", error);
    throw new Error(`Error al obtener las revisiones: ${error.message}`);
  }

  return (data as unknown as DocumentReview[]) || [];
}

/**
 * Get recent document reviews for a user (for dashboard widgets)
 */
export async function getRecentDocumentReviews(
  userId: string,
  limit: number = 5
): Promise<DocumentReview[]> {
  return getUserDocumentReviews(userId, { limit });
}

/**
 * Count document reviews for a user
 */
export async function countUserDocumentReviews(
  userId: string,
  filters?: {
    country?: string;
    documentType?: string;
    status?: AnalysisStatus;
  }
): Promise<number> {
  let query = supabase
    .from("document_reviews" as any)
    .select("id", { count: "exact", head: true })
    .eq("user_id", userId);

  if (filters?.country) {
    query = query.eq("country", filters.country);
  }

  if (filters?.documentType) {
    query = query.eq("document_type", filters.documentType);
  }

  if (filters?.status) {
    query = query.eq("analysis_status", filters.status);
  }

  const { count, error } = await query;

  if (error) {
    console.error("Error counting document reviews:", error);
    throw new Error(`Error al contar las revisiones: ${error.message}`);
  }

  return count || 0;
}

/**
 * Delete a document review
 */
export async function deleteDocumentReview(id: string): Promise<void> {
  const { error } = await supabase
    .from("document_reviews" as any)
    .delete()
    .eq("id", id);

  if (error) {
    console.error("Error deleting document review:", error);
    throw new Error(`Error al eliminar la revisión: ${error.message}`);
  }
}

/**
 * Get statistics for user's document reviews
 */
export async function getDocumentReviewStats(userId: string): Promise<{
  total: number;
  completed: number;
  pending: number;
  failed: number;
  byRisk: {
    low: number;
    medium: number;
    high: number;
  };
}> {
  const reviews = await getUserDocumentReviews(userId);

  const stats = {
    total: reviews.length,
    completed: 0,
    pending: 0,
    failed: 0,
    byRisk: {
      low: 0,
      medium: 0,
      high: 0,
    },
  };

  reviews.forEach((review) => {
    // Count by status
    if (review.analysis_status === "completed") {
      stats.completed++;
      // Count by risk level
      if (review.analysis_result?.overall_risk) {
        stats.byRisk[review.analysis_result.overall_risk]++;
      }
    } else if (review.analysis_status === "pending") {
      stats.pending++;
    } else if (review.analysis_status === "failed") {
      stats.failed++;
    }
  });

  return stats;
}
